import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

import { getContrastTextColor, hexColorValidator } from './color-picker.utils';

@Component({
  selector: 'rms-color-picker',
  templateUrl: './color-picker.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: `
    rms-color-picker input[type='color'] {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      padding: 0;
      overflow: hidden;
    }

    rms-color-picker input[type='color']::-webkit-color-swatch-wrapper {
      padding: 0;
      border-radius: 50%;
    }

    rms-color-picker input[type='color']::-webkit-color-swatch {
      border: none;
      border-radius: 50%;
    }

    rms-color-picker input[type='color']::-moz-color-swatch {
      border: none;
      border-radius: 50%;
    }
  `,
  imports: [MatError, MatFormField, MatIcon, MatInput, MatTooltip, ReactiveFormsModule, TranslatePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() public examples: string[] = [];
  @Input() public showExamples = true;

  protected readonly colorControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [hexColorValidator],
  });

  protected readonly isCustomMode = signal(false);
  protected readonly getContrastTextColor = getContrastTextColor;

  protected get safeColor(): string {
    return this.colorControl.valid && this.colorControl.value ? this.colorControl.value : '#FFFFFF';
  }

  private readonly destroyRef = inject(DestroyRef);
  private onChange?: (value: string) => void;
  private onTouched?: () => void;

  ngOnInit(): void {
    this.colorControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.onTouched?.();
      this.onChange?.(value);
    });
  }

  writeValue(value: string | null): void {
    const normalized = value ?? '';
    this.colorControl.setValue(normalized, { emitEvent: false });
    const inExamples = !!normalized && this.examples.some((c) => c.toLowerCase() === normalized.toLowerCase());
    this.isCustomMode.set(!!normalized && !inExamples);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.colorControl.disable({ emitEvent: false }) : this.colorControl.enable({ emitEvent: false });
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.colorControl.errors;
  }

  protected selectExample(color: string): void {
    this.colorControl.setValue(color);
    this.colorControl.markAsTouched();
    this.isCustomMode.set(false);
  }

  protected activateCustomMode(): void {
    const current = this.colorControl.value;
    const isExample = this.examples.some((c) => c.toLowerCase() === current.toLowerCase());
    if (isExample || !current) {
      this.colorControl.setValue('#FFFFFF');
    }
    this.isCustomMode.set(true);
  }

  protected onNativeColorChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.colorControl.setValue(input.value);
    this.colorControl.markAsTouched();
  }
}
